import { useCallback, useEffect, useMemo, useState } from 'react'
import type { FormEvent } from 'react'
import { format } from 'date-fns'
import { MessageCircle, Search } from 'lucide-react'
import { adminApi, unwrap } from '../../services/api'
import ReorderModal from '../orders/ReorderModal'
import LoadErrorState from '../common/LoadErrorState'
import {
  SERVER_ERROR_MESSAGE,
  SERVER_TIMEOUT_MESSAGE,
  withServerTimeout,
} from '../common/loadErrorUtils'
import { useColors } from '../../hooks/useColors'
import { useToast } from '../../contexts/ToastContext'

interface CustomerRow {
  id: string
  name?: string | null
  phone?: string | null
  is_active?: boolean
  created_at?: string
  order_count?: number | string | null
  total_spent?: number | string | null
  last_order_date?: string | null
  loyalty_balance?: number | string | null
  tags?: string[] | null
  customer_notes?: string | null
}

interface CustomersPayload {
  customers?: CustomerRow[]
  total?: number | string
  page?: number | string
  limit?: number | string
}

interface OrderHistoryItem {
  id: string
  order_number?: string | null
  status?: string | null
  total?: number | null
  created_at?: string | null
}

interface LoyaltyTransactionItem {
  id: string
  type?: string | null
  points?: number | null
  balance_after?: number | null
  reason?: string | null
  created_at?: string | null
  order_number?: string | null
  order_total?: number | null
  recorded_by_name?: string | null
}

interface CustomerLoyaltyPayload {
  customer?: CustomerRow
  loyalty?: {
    enabled?: boolean
    balance?: number
    earned_points?: number
    adjusted_points?: number
    settings?: {
      earn_spend_amount?: number
      earn_points?: number
      redeem_points?: number
      redeem_value?: number
      min_redeem_order_amount?: number
    }
    transactions?: LoyaltyTransactionItem[]
  }
}

export interface CustomerCLVPayload {
  total_spent: number
  total_orders: number
  avg_order_value: number
  first_order: string | null
  last_order: string | null
  days_active: number
  order_frequency_days: number
  predicted_monthly_value: number
  referrals_made: number
  referral_revenue: number
}

const PAGE_SIZE = 20
const TAG_OPTIONS = ['vip', 'corporate', 'bulk', 'new', 'regular', 'at-risk']
const TAG_STYLES: Record<string, { background: string; color: string }> = {
  vip: { background: '#FFF7D6', color: '#B45309' },
  corporate: { background: '#DBEAFE', color: '#1D4ED8' },
  bulk: { background: '#EDE9FE', color: '#7C3AED' },
  new: { background: '#DCFCE7', color: '#15803D' },
  regular: { background: 'var(--bg-secondary)', color: 'var(--text-secondary)' },
  'at-risk': { background: '#FEE2E2', color: '#991B1B' },
}

const formatCurrency = (value?: number | string | null) => {
  const amount = Number(value || 0)
  return `₹${amount.toLocaleString('en-IN')}`
}

const getInitials = (name?: string | null) => {
  if (!name) {
    return 'CU'
  }

  return name
    .split(' ')
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase()
}

const buildWelcomeMessage = (name?: string | null) =>
  `Welcome to Rapidry!\n\n` +
  `Hi ${name || 'there'}! Thank you for choosing Rapidry, Gurugram's premium laundry service.\n\n` +
  `What we offer:\n` +
  `• Wash & Fold\n` +
  `• Dry Cleaning\n` +
  `• Ironing / Steam Press\n` +
  `• Stain Treatment\n\n` +
  `Order anytime. We'll pick up from your doorstep!\n\n` +
  `First order: Use code WELCOME20 for 20% off!\n\n` +
  `Team Rapidry\n` +
  `+91 76676 25880`

const openWhatsApp = (phone: string | null | undefined, message: string, addToast: (msg: string, type?: 'success' | 'error' | 'info') => void) => {
  if (!phone) {
    addToast('Customer phone is not available for WhatsApp.', 'error')
    return
  }

  const cleanPhone = phone.replace(/[\s\-+()]/g, '')
  const phoneWithCountry = cleanPhone.startsWith('91') ? cleanPhone : `91${cleanPhone}`
  const encodedMessage = encodeURIComponent(message)
  window.open(`https://wa.me/${phoneWithCountry}?text=${encodedMessage}`, '_blank')
}

const getSafePageBounds = (page: number, total: number) => {
  if (total <= 0) {
    return { start: 0, end: 0 }
  }

  return {
    start: (page - 1) * PAGE_SIZE + 1,
    end: Math.min(page * PAGE_SIZE, total),
  }
}

export default function CustomersTab() {
  const colors = useColors()
  const { addToast } = useToast()
  const [customers, setCustomers] = useState<CustomerRow[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [createError, setCreateError] = useState('')
  const [isCreating, setIsCreating] = useState(false)
  const [showOrdersModal, setShowOrdersModal] = useState(false)
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerRow | null>(null)
  const [selectedTags, setSelectedTags] = useState<string[]>([])
  const [customerNotes, setCustomerNotes] = useState('')
  const [isSavingTags, setIsSavingTags] = useState(false)
  const [orderHistory, setOrderHistory] = useState<OrderHistoryItem[]>([])
  const [ordersLoading, setOrdersLoading] = useState(false)
  const [loyaltyLoading, setLoyaltyLoading] = useState(false)
  const [customerLoyalty, setCustomerLoyalty] = useState<CustomerLoyaltyPayload['loyalty'] | null>(null)
  const [loyaltyType, setLoyaltyType] = useState<'bonus' | 'deduction'>('bonus')
  const [loyaltyPoints, setLoyaltyPoints] = useState('')
  const [loyaltyReason, setLoyaltyReason] = useState('')
  const [isAdjustingLoyalty, setIsAdjustingLoyalty] = useState(false)
  const [clvData, setClvData] = useState<CustomerCLVPayload | null>(null)
  const [clvLoading, setClvLoading] = useState(false)
  const [showReorderModal, setShowReorderModal] = useState(false)
  const [reorderTargetId, setReorderTargetId] = useState<string | null>(null)
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    address_line: '',
  })

  const fetchCustomers = useCallback(async (page: number, search?: string) => {
    setLoading(true)
    setError('')

    try {
      const response = await withServerTimeout(adminApi.getCustomers({ page, limit: PAGE_SIZE, search: search || undefined }))
      const payload = unwrap<CustomersPayload | { customers?: CustomerRow[] } | CustomerRow[]>(response)

      if (Array.isArray(payload)) {
        setCustomers(payload)
        setTotal(payload.length)
        return
      }

      const nextCustomers = Array.isArray(payload?.customers)
        ? payload.customers
        : Array.isArray((payload as { data?: CustomersPayload })?.data?.customers)
          ? (payload as { data?: CustomersPayload }).data?.customers ?? []
          : []

      const nextTotal = Number(
        payload && typeof payload === 'object' && 'total' in payload
          ? payload.total
          : (payload as { data?: CustomersPayload })?.data?.total ?? 0,
      )

      setCustomers(nextCustomers)
      setTotal(nextTotal)
    } catch (loadError) {
      setError(loadError instanceof Error && loadError.message === SERVER_TIMEOUT_MESSAGE ? SERVER_TIMEOUT_MESSAGE : SERVER_ERROR_MESSAGE)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setCurrentPage(1)
      setDebouncedSearch(searchTerm.trim())
    }, 300)

    return () => window.clearTimeout(timeout)
  }, [searchTerm])

  useEffect(() => {
    void fetchCustomers(currentPage, debouncedSearch)
  }, [currentPage, debouncedSearch, fetchCustomers])

  const stats = useMemo(() => {
    const active = customers.filter((customer) => customer.is_active === true).length
    const totalRevenue = customers.reduce((sum, customer) => sum + Number(customer.total_spent || 0), 0)

    return {
      active,
      totalRevenue,
    }
  }, [customers])

  const handleSuspendToggle = async (id: string) => {
    await adminApi.suspendCustomer(id)
    await fetchCustomers(currentPage, debouncedSearch)
  }

  const handleCreateCustomer = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (!formData.name.trim() || !formData.phone.trim()) {
      setCreateError('Name and phone are required.')
      return
    }

    setIsCreating(true)
    setCreateError('')

    try {
      await adminApi.createCustomer({
        name: formData.name.trim(),
        phone: formData.phone.trim(),
        email: formData.email.trim() || undefined,
        address_line: formData.address_line.trim() || undefined,
      })

      setFormData({ name: '', phone: '', email: '', address_line: '' })
      setShowCreateModal(false)
      await fetchCustomers(currentPage, debouncedSearch)
    } catch (error) {
      setCreateError('Unable to create customer. Please try again.')
    } finally {
      setIsCreating(false)
    }
  }

  const loadOrderHistory = async (customer: CustomerRow) => {
    setOrdersLoading(true)
    try {
      const response = await adminApi.getOrders({ search: customer.phone || undefined, limit: 10 })
      const payload = unwrap<{ orders?: OrderHistoryItem[] } | { data?: { orders?: OrderHistoryItem[] } }>(response)
      const orders = Array.isArray((payload as { orders?: OrderHistoryItem[] }).orders)
        ? (payload as { orders?: OrderHistoryItem[] }).orders
        : Array.isArray((payload as { data?: { orders?: OrderHistoryItem[] } }).data?.orders)
          ? (payload as { data?: { orders?: OrderHistoryItem[] } }).data?.orders || []
          : []
      setOrderHistory(orders || [])
    } catch (historyError) {
      setOrderHistory([])
    } finally {
      setOrdersLoading(false)
    }
  }

  const loadCustomerLoyalty = async (customerId: string) => {
    setLoyaltyLoading(true)
    try {
      const response = await adminApi.getCustomerLoyalty(customerId)
      const payload = unwrap<CustomerLoyaltyPayload>(response)
      setCustomerLoyalty(payload?.loyalty ?? null)
      return payload?.loyalty ?? null
    } catch (error) {
      setCustomerLoyalty(null)
      return null
    } finally {
      setLoyaltyLoading(false)
    }
  }

  const handleAdjustLoyalty = async () => {
    if (!selectedCustomer) {
      return
    }

    const parsedPoints = Number(loyaltyPoints)
    if (!Number.isFinite(parsedPoints) || parsedPoints <= 0) {
      addToast('Enter a valid points value.', 'error')
      return
    }

    setIsAdjustingLoyalty(true)
    try {
      await adminApi.adjustCustomerLoyalty(selectedCustomer.id, {
        points: parsedPoints,
        type: loyaltyType,
        reason: loyaltyReason.trim() || undefined,
      })
      const nextLoyalty = await loadCustomerLoyalty(selectedCustomer.id)
      setLoyaltyPoints('')
      setLoyaltyReason('')
      setSelectedCustomer((current) =>
        current
          ? {
              ...current,
              loyalty_balance: nextLoyalty?.balance ?? current.loyalty_balance,
            }
          : current,
      )
    } finally {
      setIsAdjustingLoyalty(false)
    }
  }

  const handleToggleTag = (tag: string) => {
    setSelectedTags((current) =>
      current.includes(tag)
        ? current.filter((existingTag) => existingTag !== tag)
        : [...current, tag],
    )
  }

  const handleSaveTags = async () => {
    if (!selectedCustomer) {
      return
    }

    setIsSavingTags(true)
    try {
      await adminApi.updateCustomerTags(selectedCustomer.id, {
        tags: selectedTags,
        customer_notes: customerNotes.trim() || null,
      })

      await fetchCustomers(currentPage, debouncedSearch)
    } finally {
      setIsSavingTags(false)
    }
  }

  const loadCustomerCLV = async (customerId: string) => {
    setClvLoading(true)
    try {
      const response = await adminApi.getCustomerCLV(customerId)
      const payload = unwrap<CustomerCLVPayload>(response)
      setClvData(payload || null)
    } catch {
      setClvData(null)
    } finally {
      setClvLoading(false)
    }
  }

  const handleOpenOrders = async (customer: CustomerRow) => {
    setSelectedCustomer(customer)
    setShowOrdersModal(true)
    setSelectedTags([...(customer.tags || [])])
    setCustomerNotes(customer.customer_notes || '')
    setCustomerLoyalty(null)
    setLoyaltyPoints('')
    setLoyaltyReason('')
    setLoyaltyType('bonus')
    setClvData(null)
    await Promise.all([
      loadOrderHistory(customer), 
      loadCustomerLoyalty(customer.id),
      loadCustomerCLV(customer.id)
    ])
  }

  const handleOpenReorder = (orderId: string) => {
    setReorderTargetId(orderId)
    setShowReorderModal(true)
  }

  const { start, end } = getSafePageBounds(currentPage, total)
  const hasCustomers = customers.length > 0
  const canGoPrevious = currentPage > 1
  const canGoNext = currentPage * PAGE_SIZE < total

  return (
    <div>
      <div style={{ margin: '20px 32px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px' }}>
        <h2 className="font-display" style={{ margin: 0, fontSize: '20px', fontWeight: 600, color: colors.textPrimary }}>
          {total} Customers
        </h2>

        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{ position: 'relative', width: '280px', flexShrink: 0 }}>
            <Search size={16} color={colors.textMuted} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
            <input
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Search customers..."
              className="font-body"
              style={{
                width: '100%',
                height: '40px',
                paddingLeft: '38px',
                border: `1.5px solid ${colors.border}`,
                borderRadius: '10px',
                fontSize: '14px',
                outline: 'none',
                color: colors.textPrimary,
                background: colors.surface,
              }}
              onFocus={(event) => {
                event.currentTarget.style.borderColor = '#D6B97B'
              }}
              onBlur={(event) => {
                event.currentTarget.style.borderColor = colors.border
              }}
            />
          </div>

          <button
            type="button"
            className="font-body"
            onClick={() => setShowCreateModal(true)}
            style={{
              border: 'none',
              background: '#D6B97B',
              color: colors.textPrimary,
              fontSize: '14px',
              fontWeight: 600,
              padding: '10px 20px',
              borderRadius: '10px',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            + New Customer
          </button>
        </div>
      </div>

      <div style={{ margin: '0 32px 20px', display: 'flex', gap: '12px' }}>
        <div style={{ flex: 1, background: colors.surface, borderRadius: '12px', padding: '16px', border: `1.5px solid ${colors.border}` }}>
          <p className="font-body" style={{ margin: '0 0 6px', fontSize: '11px', fontWeight: 600, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Total Customers
          </p>
          <p className="font-display" style={{ margin: 0, fontSize: '28px', fontWeight: 700, color: colors.textPrimary }}>
            {total}
          </p>
        </div>

        <div style={{ flex: 1, background: colors.surface, borderRadius: '12px', padding: '16px', border: `1.5px solid ${colors.border}` }}>
          <p className="font-body" style={{ margin: '0 0 6px', fontSize: '11px', fontWeight: 600, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Active
          </p>
          <p className="font-display" style={{ margin: 0, fontSize: '28px', fontWeight: 700, color: '#15803D' }}>
            {stats.active}
          </p>
        </div>

        <div style={{ flex: 1, background: colors.surface, borderRadius: '12px', padding: '16px', border: `1.5px solid ${colors.border}` }}>
          <p className="font-body" style={{ margin: '0 0 6px', fontSize: '11px', fontWeight: 600, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Total Revenue
          </p>
          <p className="font-display" style={{ margin: 0, fontSize: '28px', fontWeight: 700, color: '#D6B97B' }}>
            {formatCurrency(stats.totalRevenue)}
          </p>
        </div>
      </div>

      <div
        style={{
          background: colors.surface,
          borderRadius: '16px',
          margin: '0 32px 0',
          boxShadow: '0px 2px 8px rgba(0,0,0,0.04)',
          overflow: 'hidden',
        }}
      >
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: colors.bgSecondary, borderBottom: `1px solid ${colors.border}` }}>
              {['#', 'Avatar', 'Name', 'Phone', 'Orders', 'Total Spent', 'Joined', 'Actions'].map((header) => (
                <th
                  key={header}
                  className="font-body"
                  style={{
                    padding: '16px 20px',
                    fontSize: '11px',
                    fontWeight: 600,
                    color: colors.textMuted,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    textAlign: 'left',
                  }}
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {error && !loading ? (
              <tr>
                <td colSpan={8}>
                  <LoadErrorState message={error} onRetry={() => void fetchCustomers(currentPage, debouncedSearch)} margin="24px" />
                </td>
              </tr>
            ) : loading ? (
              <tr>
                <td colSpan={8} style={{ padding: '32px 20px', textAlign: 'center' }}>
                  <p className="font-body" style={{ margin: 0, fontSize: '14px', color: colors.textMuted }}>
                    Loading customers...
                  </p>
                </td>
              </tr>
            ) : !hasCustomers ? (
              <tr>
                <td colSpan={8} style={{ padding: '32px 20px', textAlign: 'center' }}>
                  <p className="font-body" style={{ margin: 0, fontSize: '14px', color: colors.textMuted }}>
                    No customers found.
                  </p>
                </td>
              </tr>
            ) : (
              customers.map((customer, index) => {
                const isActive = customer.is_active !== false
                const rowNumber = (currentPage - 1) * PAGE_SIZE + index + 1

                return (
                  <tr
                    key={customer.id}
                    style={{ borderBottom: `0.5px solid ${colors.surface}` }}
                    onMouseEnter={(event) => {
                      event.currentTarget.style.background = colors.bgSecondary
                    }}
                    onMouseLeave={(event) => {
                      event.currentTarget.style.background = 'transparent'
                    }}
                  >
                    <td className="font-body" style={{ padding: '16px 20px', width: '40px', fontSize: '13px', fontWeight: 400, color: colors.textMuted }}>
                      {rowNumber}
                    </td>
                    <td style={{ padding: '16px 20px' }}>
                      <div
                        className="font-body"
                        style={{
                          width: '32px',
                          height: '32px',
                          borderRadius: '999px',
                          background: '#D6B97B',
                          color: colors.textPrimary,
                          display: 'grid',
                          placeItems: 'center',
                          fontSize: '11px',
                          fontWeight: 600,
                        }}
                      >
                        {getInitials(customer.name)}
                      </div>
                    </td>
                    <td className="font-body" style={{ padding: '16px 20px', fontSize: '14px', fontWeight: 600, color: colors.textPrimary }}>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center' }}>
                        <span>{customer.name || 'Customer'}</span>
                        {Number(customer.loyalty_balance || 0) > 0 ? (
                          <span
                            className="font-body"
                            style={{
                              padding: '2px 8px',
                              borderRadius: '999px',
                              fontSize: '11px',
                              fontWeight: 700,
                              background: '#ECFDF5',
                              color: '#15803D',
                            }}
                          >
                            {Number(customer.loyalty_balance || 0)} pts
                          </span>
                        ) : null}
                        {(customer.tags || []).map((tag) => {
                          const style = TAG_STYLES[tag] || { background: 'var(--bg-secondary)', color: 'var(--text-secondary)' }
                          return (
                            <span
                              key={`${customer.id}-${tag}`}
                              className="font-body"
                              style={{
                                padding: '2px 8px',
                                borderRadius: '999px',
                                fontSize: '11px',
                                fontWeight: 600,
                                background: style.background,
                                color: style.color,
                                textTransform: 'capitalize',
                              }}
                            >
                              {tag.replace('-', ' ')}
                            </span>
                          )
                        })}
                      </div>
                    </td>
                    <td className="font-body" style={{ padding: '16px 20px', fontSize: '13px', fontWeight: 400, color: colors.textSecondary }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span>{customer.phone || '-'}</span>
                        <button
                          type="button"
                          aria-label="Send WhatsApp"
                          onClick={() => {
                            openWhatsApp(customer.phone, buildWelcomeMessage(customer.name), addToast)
                          }}
                          style={{
                            width: '26px',
                            height: '26px',
                            borderRadius: '8px',
                            border: 'none',
                            background: '#25D366',
                            color: colors.bgCard,
                            fontSize: '12px',
                            cursor: 'pointer',
                            display: 'grid',
                            placeItems: 'center',
                          }}
                          onMouseEnter={(event) => {
                            event.currentTarget.style.background = '#1EAD54'
                          }}
                          onMouseLeave={(event) => {
                            event.currentTarget.style.background = '#25D366'
                          }}
                        >
                          <MessageCircle size={12} />
                        </button>
                      </div>
                    </td>
                    <td className="font-body" style={{ padding: '16px 20px', fontSize: '14px', fontWeight: 600, color: colors.textPrimary }}>
                      {Number(customer.order_count || 0)}
                    </td>
                    <td className="font-body" style={{ padding: '16px 20px', fontSize: '14px', fontWeight: 600, color: '#D6B97B' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {formatCurrency(customer.total_spent)}
                        {Number(customer.total_spent || 0) > 15000 ? (
                          <span style={{ fontSize: '14px' }}>🥇</span>
                        ) : Number(customer.total_spent || 0) > 5000 ? (
                          <span style={{ fontSize: '14px' }}>🥈</span>
                        ) : Number(customer.total_spent || 0) > 0 ? (
                          <span style={{ fontSize: '14px' }}>🥉</span>
                        ) : null}
                      </div>
                    </td>
                    <td className="font-body" style={{ padding: '16px 20px', fontSize: '13px', fontWeight: 400, color: colors.textSecondary }}>
                      {customer.created_at ? format(new Date(customer.created_at), 'MMM dd, yyyy') : '-'}
                    </td>
                    <td style={{ padding: '16px 20px' }}>
                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                        <button
                          type="button"
                          className="font-body"
                          style={{
                            border: `1px solid ${colors.border}`,
                            borderRadius: '6px',
                            padding: '4px 10px',
                            fontSize: '12px',
                            fontWeight: 600,
                            color: colors.textPrimary,
                            background: 'transparent',
                            cursor: 'pointer',
                          }}
                          onClick={() => {
                            void handleOpenOrders(customer)
                          }}
                          onMouseEnter={(event) => {
                            event.currentTarget.style.background = colors.surface
                          }}
                          onMouseLeave={(event) => {
                            event.currentTarget.style.background = 'transparent'
                          }}
                        >
                          View
                        </button>
                        <button
                          type="button"
                          className="font-body"
                          onClick={() => {
                            void handleSuspendToggle(customer.id)
                          }}
                          style={{
                            border: `1px solid ${isActive ? '#FCA5A5' : '#86EFAC'}`,
                            borderRadius: '6px',
                            padding: '4px 10px',
                            fontSize: '12px',
                            fontWeight: 600,
                            color: isActive ? '#991B1B' : '#15803D',
                            background: 'transparent',
                            cursor: 'pointer',
                          }}
                          onMouseEnter={(event) => {
                            event.currentTarget.style.background = isActive ? '#FEF2F2' : '#F0FDF4'
                          }}
                          onMouseLeave={(event) => {
                            event.currentTarget.style.background = 'transparent'
                          }}
                        >
                          {isActive ? 'Suspend' : 'Activate'}
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>

      <div style={{ padding: '16px 32px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px' }}>
        <p className="font-body" style={{ margin: 0, fontSize: '13px', fontWeight: 400, color: colors.textMuted }}>
          Showing {start}-{end} of {total} customers
        </p>

        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            type="button"
            className="font-body"
            disabled={!canGoPrevious}
            onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
            style={{
              border: `1px solid ${colors.border}`,
              borderRadius: '8px',
              padding: '6px 14px',
              fontSize: '13px',
              fontWeight: 500,
              color: colors.textPrimary,
              background: colors.bgCard,
              cursor: canGoPrevious ? 'pointer' : 'not-allowed',
              opacity: canGoPrevious ? 1 : 0.4,
            }}
          >
            Previous
          </button>
          <button
            type="button"
            className="font-body"
            disabled={!canGoNext}
            onClick={() => setCurrentPage((page) => page + 1)}
            style={{
              border: `1px solid ${colors.border}`,
              borderRadius: '8px',
              padding: '6px 14px',
              fontSize: '13px',
              fontWeight: 500,
              color: colors.textPrimary,
              background: colors.bgCard,
              cursor: canGoNext ? 'pointer' : 'not-allowed',
              opacity: canGoNext ? 1 : 0.4,
            }}
          >
            Next
          </button>
        </div>
      </div>

      {showCreateModal ? (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 60,
            background: 'rgba(0,0,0,0.35)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '24px',
          }}
          onClick={() => setShowCreateModal(false)}
        >
          <form
            onSubmit={(event) => {
              void handleCreateCustomer(event)
            }}
            onClick={(event) => event.stopPropagation()}
            style={{
              width: '420px',
              background: colors.bgCard,
              borderRadius: '20px',
              boxShadow: '0px 24px 64px rgba(15,46,42,0.20)',
              padding: '28px',
            }}
          >
            <h3 className="font-display" style={{ margin: '0 0 16px', fontSize: '20px', fontWeight: 600, color: colors.textPrimary }}>
              Add Customer
            </h3>

            {createError ? (
              <div className="font-body" style={{ marginBottom: '12px', background: '#FEF2F2', border: '1px solid #FCA5A5', color: '#991B1B', padding: '10px 12px', borderRadius: '10px', fontSize: '12px', fontWeight: 600 }}>
                {createError}
              </div>
            ) : null}

            <div style={{ display: 'grid', gap: '12px' }}>
              <input
                required
                value={formData.name}
                onChange={(event) => setFormData((prev) => ({ ...prev, name: event.target.value }))}
                placeholder="Customer name"
                className="font-body"
                style={{ height: '44px', border: `1.5px solid ${colors.border}`, borderRadius: '10px', padding: '0 14px', fontSize: '14px' }}
              />
              <input
                required
                value={formData.phone}
                onChange={(event) => setFormData((prev) => ({ ...prev, phone: event.target.value }))}
                placeholder="Phone number"
                className="font-body"
                style={{ height: '44px', border: `1.5px solid ${colors.border}`, borderRadius: '10px', padding: '0 14px', fontSize: '14px' }}
              />
              <input
                value={formData.email}
                onChange={(event) => setFormData((prev) => ({ ...prev, email: event.target.value }))}
                placeholder="Email (optional)"
                className="font-body"
                style={{ height: '44px', border: `1.5px solid ${colors.border}`, borderRadius: '10px', padding: '0 14px', fontSize: '14px' }}
              />
              <input
                value={formData.address_line}
                onChange={(event) => setFormData((prev) => ({ ...prev, address_line: event.target.value }))}
                placeholder="Address"
                className="font-body"
                style={{ height: '44px', border: `1.5px solid ${colors.border}`, borderRadius: '10px', padding: '0 14px', fontSize: '14px' }}
              />
            </div>

            <div style={{ display: 'flex', gap: '10px', marginTop: '18px' }}>
              <button
                type="button"
                className="font-body"
                onClick={() => setShowCreateModal(false)}
                style={{ flex: 1, height: '42px', border: `1.5px solid ${colors.border}`, borderRadius: '10px', background: 'transparent', fontSize: '14px', fontWeight: 600, color: colors.textSecondary, cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="font-body"
                disabled={isCreating}
                style={{ flex: 1, height: '42px', border: 'none', borderRadius: '10px', background: '#D6B97B', fontSize: '14px', fontWeight: 600, color: colors.textPrimary, cursor: 'pointer', opacity: isCreating ? 0.6 : 1 }}
              >
                {isCreating ? 'Creating...' : 'Create Customer'}
              </button>
            </div>
          </form>
        </div>
      ) : null}

      {showOrdersModal && selectedCustomer ? (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 60,
            background: 'rgba(0,0,0,0.35)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '24px',
          }}
          onClick={() => setShowOrdersModal(false)}
        >
          <div
            onClick={(event) => event.stopPropagation()}
            style={{
              width: '640px',
              maxHeight: '80vh',
              overflowY: 'auto',
              background: colors.bgCard,
              borderRadius: '18px',
              padding: '24px',
              boxShadow: '0px 24px 64px rgba(15,46,42,0.20)',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <div>
                <h3 className="font-display" style={{ margin: 0, fontSize: '18px', color: colors.textPrimary }}>
                  Order History
                </h3>
                <p className="font-body" style={{ margin: '4px 0 0', color: colors.textMuted, fontSize: '12px' }}>
                  {selectedCustomer.name || selectedCustomer.phone}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowOrdersModal(false)}
                style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '18px' }}
              >
                ✕
              </button>
            </div>

            <div style={{ display: 'grid', gap: '12px', marginBottom: '16px' }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                {TAG_OPTIONS.map((tag) => {
                  const active = selectedTags.includes(tag)
                  const style = TAG_STYLES[tag] || { background: 'var(--bg-secondary)', color: 'var(--text-secondary)' }
                  return (
                    <button
                      key={tag}
                      type="button"
                      onClick={() => handleToggleTag(tag)}
                      className="font-body"
                      style={{
                        border: `1px solid ${active ? style.color : colors.border}`,
                        background: active ? style.background : colors.bgCard,
                        color: active ? style.color : colors.textSecondary,
                        borderRadius: '999px',
                        padding: '4px 10px',
                        fontSize: '12px',
                        fontWeight: 600,
                        cursor: 'pointer',
                        textTransform: 'capitalize',
                      }}
                    >
                      {tag.replace('-', ' ')}
                    </button>
                  )
                })}
              </div>

              <textarea
                value={customerNotes}
                onChange={(event) => setCustomerNotes(event.target.value)}
                className="font-body"
                placeholder="Customer notes"
                style={{ width: '100%', minHeight: '80px', border: `1.5px solid ${colors.border}`, borderRadius: '10px', padding: '10px 12px' }}
              />

              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: '150px', background: colors.bgSecondary, borderRadius: '10px', padding: '10px 12px' }}>
                  <p className="font-body" style={{ margin: '0 0 4px', fontSize: '11px', color: colors.textMuted }}>Total Spent</p>
                  <p className="font-display" style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: colors.textPrimary }}>
                    {formatCurrency(selectedCustomer.total_spent)}
                  </p>
                </div>
                <div style={{ flex: 1, minWidth: '150px', background: colors.bgSecondary, borderRadius: '10px', padding: '10px 12px' }}>
                  <p className="font-body" style={{ margin: '0 0 4px', fontSize: '11px', color: colors.textMuted }}>Order Count</p>
                  <p className="font-display" style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: colors.textPrimary }}>
                    {Number(selectedCustomer.order_count || 0)}
                  </p>
                </div>
                <div style={{ flex: 1, minWidth: '150px', background: colors.bgSecondary, borderRadius: '10px', padding: '10px 12px' }}>
                  <p className="font-body" style={{ margin: '0 0 4px', fontSize: '11px', color: colors.textMuted }}>Last Order</p>
                  <p className="font-display" style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: colors.textPrimary }}>
                    {selectedCustomer.last_order_date ? format(new Date(selectedCustomer.last_order_date), 'MMM dd, yyyy') : '-'}
                  </p>
                </div>
              </div>


              {/* Lifetime Value (CLV) Section */}
              <div style={{ border: `1.5px solid ${colors.border}`, borderRadius: '14px', padding: '16px', background: colors.bgSecondary, marginBottom: '16px' }}>
                <p className="font-body" style={{ margin: '0 0 12px', fontSize: '11px', fontWeight: 700, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Customer Lifetime Value
                </p>
                {clvLoading ? (
                  <p className="font-body" style={{ color: colors.textMuted, fontSize: '13px', margin: 0 }}>Loading CLV data...</p>
                ) : clvData ? (
                  <>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '12px', marginBottom: '16px' }}>
                      <div>
                        <p className="font-body" style={{ margin: '0 0 4px', fontSize: '11px', color: colors.textMuted }}>Total Spent</p>
                        <p className="font-display" style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: colors.textPrimary }}>
                          {formatCurrency(clvData.total_spent)}
                        </p>
                      </div>
                      <div>
                        <p className="font-body" style={{ margin: '0 0 4px', fontSize: '11px', color: colors.textMuted }}>Total Orders</p>
                        <p className="font-display" style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: colors.textPrimary }}>
                          {clvData.total_orders}
                        </p>
                      </div>
                      <div>
                        <p className="font-body" style={{ margin: '0 0 4px', fontSize: '11px', color: colors.textMuted }}>Avg Order Value</p>
                        <p className="font-display" style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: colors.textPrimary }}>
                          {formatCurrency(clvData.avg_order_value)}
                        </p>
                      </div>
                      <div>
                        <p className="font-body" style={{ margin: '0 0 4px', fontSize: '11px', color: colors.textMuted }}>Order Freq.</p>
                        <p className="font-display" style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: colors.textPrimary }}>
                          {clvData.order_frequency_days > 0 ? `${clvData.order_frequency_days.toFixed(1)} days` : '-'}
                        </p>
                      </div>
                      <div>
                        <p className="font-body" style={{ margin: '0 0 4px', fontSize: '11px', color: colors.textMuted }}>Predicted Monthly</p>
                        <p className="font-display" style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: colors.textPrimary }}>
                          {formatCurrency(clvData.predicted_monthly_value)}
                        </p>
                      </div>
                    </div>
                    
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={{ flex: 1, height: '8px', background: colors.border, borderRadius: '4px', overflow: 'hidden' }}>
                        <div style={{ 
                          height: '100%', 
                          background: clvData.total_spent > 15000 ? '#D4AF37' : clvData.total_spent > 5000 ? '#C0C0C0' : '#CD7F32', 
                          width: `${Math.min(100, (clvData.total_spent / 20000) * 100)}%` 
                        }} />
                      </div>
                      <span className="font-body" style={{ fontSize: '12px', fontWeight: 600, color: colors.textSecondary }}>
                        {clvData.total_spent > 15000 ? '🥇 Gold Tier (>₹15K)' : clvData.total_spent > 5000 ? '🥈 Silver Tier (₹5K-₹15K)' : '🥉 Bronze Tier (<₹5K)'}
                      </span>
                    </div>
                  </>
                ) : (
                  <p className="font-body" style={{ color: colors.textMuted, fontSize: '13px', margin: 0 }}>No CLV data available.</p>
                )}
              </div>

              {/* Referral Code Section */}
              <div style={{ border: `1.5px solid ${colors.border}`, borderRadius: '14px', padding: '16px', background: colors.bgSecondary, marginBottom: '16px' }}>
                <p className="font-body" style={{ margin: '0 0 12px', fontSize: '11px', fontWeight: 700, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Referral Program
                </p>
                {clvLoading ? (
                  <p className="font-body" style={{ color: colors.textMuted, fontSize: '13px', margin: 0 }}>Loading...</p>
                ) : (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', alignItems: 'center' }}>
                    <div>
                      <p className="font-body" style={{ margin: '0 0 4px', fontSize: '11px', color: colors.textMuted }}>Referral Code</p>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <code style={{ background: colors.border, padding: '4px 8px', borderRadius: '4px', fontSize: '14px', fontWeight: 600 }}>
                          {(selectedCustomer as any).referral_code || 'RD' + getInitials(selectedCustomer.name) + '0000'}
                        </code>
                        <button
                          type="button"
                          className="font-body"
                          onClick={() => {
                            const code = (selectedCustomer as any).referral_code || 'RD' + getInitials(selectedCustomer.name) + '0000'
                            navigator.clipboard.writeText(code)
                            addToast('Copied to clipboard!', 'info')
                          }}
                          style={{ border: '1px solid #D6B97B', background: 'transparent', color: '#D6B97B', borderRadius: '6px', padding: '4px 10px', fontSize: '12px', cursor: 'pointer', fontWeight: 600 }}
                        >
                          Copy
                        </button>
                      </div>
                    </div>
                    
                    <div>
                      <p className="font-body" style={{ margin: '0 0 4px', fontSize: '11px', color: colors.textMuted }}>Referrals Made</p>
                      <p className="font-display" style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: colors.textPrimary }}>
                        {clvData?.referrals_made || 0}
                      </p>
                    </div>

                    <button
                      type="button"
                      className="font-body"
                      onClick={() => {
                        const code = (selectedCustomer as any).referral_code || 'RD' + getInitials(selectedCustomer.name) + '0000'
                        const msg = `Hey! I use Rapidry for my laundry and it's awesome. Use my code ${code} to get a special discount on your first order! Book here: https://rapidry.in`
                        openWhatsApp(selectedCustomer.phone, msg, addToast)
                      }}
                      style={{ height: '36px', border: 'none', borderRadius: '8px', background: '#25D366', color: colors.bgCard, padding: '0 16px', fontSize: '13px', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
                    >
                      <MessageCircle size={12} /> Send Invite
                    </button>
                  </div>
                )}
              </div>

              <div style={{ border: `1.5px solid ${colors.border}`, borderRadius: '14px', padding: '16px', background: colors.surface }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', marginBottom: '14px' }}>
                  <div>
                    <p className="font-body" style={{ margin: '0 0 4px', fontSize: '11px', fontWeight: 700, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Loyalty
                    </p>
                    <h4 className="font-display" style={{ margin: 0, fontSize: '18px', color: colors.textPrimary }}>
                      {loyaltyLoading ? 'Loading...' : `${Number(customerLoyalty?.balance ?? selectedCustomer.loyalty_balance ?? 0)} points`}
                    </h4>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <p className="font-body" style={{ margin: 0, fontSize: '12px', color: colors.textMuted }}>
                      {customerLoyalty?.settings
                        ? `₹${customerLoyalty.settings.earn_spend_amount || 100} = ${customerLoyalty.settings.earn_points || 10} points`
                        : 'Loyalty rules from settings'}
                    </p>
                    <p className="font-body" style={{ margin: '4px 0 0', fontSize: '12px', color: colors.textMuted }}>
                      Redeem {customerLoyalty?.settings?.redeem_points || 100} points for ₹{customerLoyalty?.settings?.redeem_value || 50}
                    </p>
                  </div>
                </div>

                <div style={{ display: 'grid', gap: '12px', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', marginBottom: '16px' }}>
                  <div style={{ background: colors.bgCard, borderRadius: '10px', padding: '10px 12px', border: `1px solid ${colors.border}` }}>
                    <p className="font-body" style={{ margin: '0 0 4px', fontSize: '11px', color: colors.textMuted }}>Earned</p>
                    <p className="font-display" style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: '#15803D' }}>
                      {customerLoyalty?.earned_points || 0}
                    </p>
                  </div>
                  <div style={{ background: colors.bgCard, borderRadius: '10px', padding: '10px 12px', border: `1px solid ${colors.border}` }}>
                    <p className="font-body" style={{ margin: '0 0 4px', fontSize: '11px', color: colors.textMuted }}>Manual Adjustments</p>
                    <p className="font-display" style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: '#D97706' }}>
                      {customerLoyalty?.adjusted_points || 0}
                    </p>
                  </div>
                  <div style={{ background: colors.bgCard, borderRadius: '10px', padding: '10px 12px', border: `1px solid ${colors.border}` }}>
                    <p className="font-body" style={{ margin: '0 0 4px', fontSize: '11px', color: colors.textMuted }}>Program</p>
                    <p className="font-display" style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: customerLoyalty?.enabled ? '#15803D' : '#991B1B' }}>
                      {customerLoyalty?.enabled ? 'Enabled' : 'Disabled'}
                    </p>
                  </div>
                </div>

                <div style={{ display: 'grid', gap: '12px', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', marginBottom: '12px' }}>
                  <button
                    type="button"
                    onClick={() => setLoyaltyType('bonus')}
                    style={{
                      height: '40px',
                      borderRadius: '10px',
                      border: '1px solid',
                      borderColor: loyaltyType === 'bonus' ? '#15803D' : colors.border,
                      background: loyaltyType === 'bonus' ? '#F0FDF4' : colors.bgCard,
                      color: '#15803D',
                      fontWeight: 700,
                      cursor: 'pointer',
                    }}
                  >
                    Add Points
                  </button>
                  <button
                    type="button"
                    onClick={() => setLoyaltyType('deduction')}
                    style={{
                      height: '40px',
                      borderRadius: '10px',
                      border: '1px solid',
                      borderColor: loyaltyType === 'deduction' ? '#991B1B' : colors.border,
                      background: loyaltyType === 'deduction' ? '#FEF2F2' : colors.bgCard,
                      color: '#991B1B',
                      fontWeight: 700,
                      cursor: 'pointer',
                    }}
                  >
                    Deduct Points
                  </button>
                  <input
                    value={loyaltyPoints}
                    onChange={(event) => setLoyaltyPoints(event.target.value)}
                    type="number"
                    min="1"
                    placeholder="Points"
                    className="font-body"
                    style={{ height: '40px', border: `1.5px solid ${colors.border}`, borderRadius: '10px', padding: '0 12px', fontSize: '14px' }}
                  />
                </div>

                <textarea
                  value={loyaltyReason}
                  onChange={(event) => setLoyaltyReason(event.target.value)}
                  placeholder="Reason for manual adjustment"
                  className="font-body"
                  style={{ width: '100%', minHeight: '64px', border: `1.5px solid ${colors.border}`, borderRadius: '10px', padding: '10px 12px', marginBottom: '12px' }}
                />

                <button
                  type="button"
                  className="font-body"
                  onClick={() => void handleAdjustLoyalty()}
                  disabled={isAdjustingLoyalty}
                  style={{
                    height: '40px',
                    borderRadius: '10px',
                    border: 'none',
                    background: loyaltyType === 'bonus' ? '#15803D' : '#991B1B',
                    color: colors.bgCard,
                    fontWeight: 700,
                    cursor: isAdjustingLoyalty ? 'not-allowed' : 'pointer',
                    opacity: isAdjustingLoyalty ? 0.6 : 1,
                  }}
                >
                  {isAdjustingLoyalty ? 'Updating...' : loyaltyType === 'bonus' ? 'Add Loyalty Points' : 'Deduct Loyalty Points'}
                </button>

                <div style={{ marginTop: '14px' }}>
                  <p className="font-body" style={{ margin: '0 0 10px', fontSize: '12px', fontWeight: 700, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Recent Loyalty Activity
                  </p>
                  {loyaltyLoading ? (
                    <p className="font-body" style={{ margin: 0, fontSize: '13px', color: colors.textMuted }}>Loading loyalty history...</p>
                  ) : (customerLoyalty?.transactions || []).length === 0 ? (
                    <p className="font-body" style={{ margin: 0, fontSize: '13px', color: colors.textMuted }}>No loyalty activity yet.</p>
                  ) : (
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ background: colors.surface, borderBottom: `1px solid ${colors.border}` }}>
                          {['Date', 'Type', 'Points', 'Balance', 'Order'].map((header) => (
                            <th key={header} className="font-body" style={{ padding: '8px 10px', fontSize: '11px', color: colors.textMuted, textAlign: 'left' }}>
                              {header}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {(customerLoyalty?.transactions || []).slice(0, 5).map((transaction) => (
                          <tr key={transaction.id} style={{ borderBottom: `1px solid ${colors.surface}` }}>
                            <td className="font-body" style={{ padding: '8px 10px', fontSize: '12px', color: colors.textSecondary }}>
                              {transaction.created_at ? format(new Date(transaction.created_at), 'MMM dd, yyyy') : '-'}
                            </td>
                            <td className="font-body" style={{ padding: '8px 10px', fontSize: '12px', fontWeight: 600, color: transaction.type === 'earn' ? '#15803D' : '#991B1B' }}>
                              {transaction.type || '-'}
                            </td>
                            <td className="font-body" style={{ padding: '8px 10px', fontSize: '12px', color: colors.textPrimary }}>
                              {transaction.points ?? 0}
                            </td>
                            <td className="font-body" style={{ padding: '8px 10px', fontSize: '12px', color: colors.textPrimary }}>
                              {transaction.balance_after ?? 0}
                            </td>
                            <td className="font-body" style={{ padding: '8px 10px', fontSize: '12px', color: colors.textSecondary }}>
                              {transaction.order_number || transaction.reason || '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>

              <button
                type="button"
                className="font-body"
                onClick={() => void handleSaveTags()}
                disabled={isSavingTags}
                style={{
                  height: '40px',
                  borderRadius: '10px',
                  border: 'none',
                  background: colors.textPrimary,
                  color: colors.bgCard,
                  fontWeight: 600,
                  cursor: 'pointer',
                  opacity: isSavingTags ? 0.6 : 1,
                }}
              >
                {isSavingTags ? 'Saving...' : 'Save Tags & Notes'}
              </button>
            </div>

            {ordersLoading ? (
              <p className="font-body" style={{ color: colors.textMuted }}>Loading orders...</p>
            ) : orderHistory.length === 0 ? (
              <p className="font-body" style={{ color: colors.textMuted }}>No past orders found.</p>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: colors.bgSecondary, borderBottom: `1px solid ${colors.border}` }}>
                    {['Order', 'Date', 'Status', 'Total', ''].map((header) => (
                      <th key={header} className="font-body" style={{ padding: '10px 12px', fontSize: '11px', color: colors.textMuted, textAlign: 'left' }}>
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {orderHistory.map((order) => (
                    <tr key={order.id} style={{ borderBottom: `1px solid ${colors.surface}` }}>
                      <td className="font-body" style={{ padding: '10px 12px', fontWeight: 600 }}>{order.order_number || '-'}</td>
                      <td className="font-body" style={{ padding: '10px 12px', color: colors.textSecondary }}>
                        {order.created_at ? format(new Date(order.created_at), 'MMM dd, yyyy') : '-'}
                      </td>
                      <td className="font-body" style={{ padding: '10px 12px', color: colors.textSecondary }}>{order.status || '-'}</td>
                      <td className="font-body" style={{ padding: '10px 12px', color: colors.textPrimary }}>{formatCurrency(order.total)}</td>
                      <td style={{ padding: '10px 12px' }}>
                        <button
                          type="button"
                          className="font-body"
                          onClick={() => handleOpenReorder(order.id)}
                          style={{ border: '1px solid #D6B97B', color: '#D6B97B', background: 'transparent', borderRadius: '8px', padding: '6px 10px', fontWeight: 700, cursor: 'pointer' }}
                        >
                          🔄 Reorder
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      ) : null}

      <ReorderModal
        isOpen={showReorderModal}
        orderId={reorderTargetId}
        onClose={() => {
          setShowReorderModal(false)
          setReorderTargetId(null)
        }}
        onSuccess={() => {
          if (selectedCustomer) {
            void loadOrderHistory(selectedCustomer)
          }
        }}
      />
    </div>
  )
}
